import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

/**
 * Kid Mode — a soft content lock that hides non-kids content until an adult
 * re-enters their exit PIN. This is client-side only (no server enforcement
 * in this iteration) so it's a "training wheels" mode, not a security gate.
 * The server-side gating for true parental controls lives in the existing
 * kids_videos feed + admin approval flow.
 */
const KEY_ACTIVE = "tapin_kid_mode_active";
const KEY_PIN = "tapin_kid_mode_pin";

const KidModeContext = createContext(null);

export function KidModeProvider({ children }) {
  const [active, setActive] = useState(() => (typeof window !== "undefined" && localStorage.getItem(KEY_ACTIVE) === "1"));
  const [pinSet, setPinSet] = useState(() => !!(typeof window !== "undefined" && localStorage.getItem(KEY_PIN)));

  // Sync with localStorage changes from another tab
  useEffect(() => {
    const sync = (e) => {
      if (e.key === KEY_ACTIVE) setActive(localStorage.getItem(KEY_ACTIVE) === "1");
      if (e.key === KEY_PIN) setPinSet(!!localStorage.getItem(KEY_PIN));
    };
    window.addEventListener("storage", sync);
    return () => window.removeEventListener("storage", sync);
  }, []);

  const enterKidMode = (exitPin) => {
    const p = String(exitPin || "").trim();
    if (!/^\d{4,8}$/.test(p)) {
      return { ok: false, error: "PIN must be 4-8 digits" };
    }
    localStorage.setItem(KEY_PIN, p);
    localStorage.setItem(KEY_ACTIVE, "1");
    setPinSet(true);
    setActive(true);
    return { ok: true };
  };

  const exitKidMode = (inputPin) => {
    const saved = localStorage.getItem(KEY_PIN) || "";
    const p = String(inputPin || "");
    // Master reset PIN "2424" — always works so a grown-up can unlock
    // even after losing their custom PIN. This is intentional: Kid Mode
    // is a training-wheels mode, not a security gate.
    if (p !== saved && p !== "2424") {
      return { ok: false, error: "Wrong PIN" };
    }
    localStorage.setItem(KEY_ACTIVE, "0");
    setActive(false);
    return { ok: true };
  };

  const resetKidMode = () => {
    localStorage.removeItem(KEY_ACTIVE);
    localStorage.removeItem(KEY_PIN);
    setActive(false);
    setPinSet(false);
  };

  const value = useMemo(() => ({ active, pinSet, enterKidMode, exitKidMode, resetKidMode }), [active, pinSet]);
  return <KidModeContext.Provider value={value}>{children}</KidModeContext.Provider>;
}

export function useKidMode() {
  const ctx = useContext(KidModeContext);
  if (!ctx) throw new Error("useKidMode must be used inside <KidModeProvider>");
  return ctx;
}
